"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

/**
 * The tap cycle, shown instead of described.
 *
 * One square from the board, drawn with the game's own visual grammar, stepping through what
 * repeated taps do: place → might be → not here → clear. It advances by itself so the how-to-play
 * page demonstrates without being touched — unless the reader prefers reduced motion, in which
 * case the buttons do the stepping and nothing moves on its own.
 */
const STEPS = ["place", "maybe", "no", "clear"] as const;
type Step = (typeof STEPS)[number];

const HOLD_MS = 1600;

export function TapCycleDemo() {
  const t = useTranslations("howToPlay.demo");
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    setAuto(true);
    const timer = setInterval(() => setIndex((current) => (current + 1) % STEPS.length), HOLD_MS);
    return () => clearInterval(timer);
  }, []);

  const step: Step = STEPS[index] as Step;

  return (
    <figure data-testid="tap-cycle-demo" style={{ margin: "1.5rem 0", display: "grid", gap: "0.6rem", justifyItems: "start" }}>
      <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
        <svg width={88} height={88} viewBox="0 0 88 88" aria-hidden="true">
          {/* The square, in the classic room colours. */}
          <rect x={2} y={2} width={84} height={84} fill="#e8ded0" stroke="#2b2823" strokeWidth={4} />

          {step === "place" && (
            <g data-step="place">
              <circle cx={44} cy={44} r={26} fill="#1b1a17" />
              <text x={44} y={53} textAnchor="middle" fontSize={26} fontWeight={700} fill="#fff">
                A
              </text>
            </g>
          )}
          {step === "maybe" && (
            <text data-step="maybe" x={24} y={34} fontSize={20} fill="#3f3b35">
              A
            </text>
          )}
          {step === "no" && (
            <g data-step="no">
              <text x={24} y={34} fontSize={20} fill="#9a5b52" opacity={0.9}>
                A
              </text>
              <line x1={14} y1={27} x2={34} y2={27} stroke="#9a5b52" strokeWidth={2.5} />
            </g>
          )}
          {/* "clear": the square as it started — nothing drawn on it. */}
        </svg>

        <div>
          <p data-testid="demo-caption" style={{ margin: 0, fontWeight: 600 }}>
            {index + 1}. {t(`${step}Title`)}
          </p>
          <p style={{ margin: 0, color: "var(--muted)", maxWidth: "26rem" }}>{t(`${step}Body`)}</p>
        </div>
      </div>

      {!auto && (
        <div style={{ display: "flex", gap: "0.4rem" }}>
          {STEPS.map((name, stepIndex) => (
            <button
              key={name}
              type="button"
              data-testid={`demo-step-${stepIndex}`}
              onClick={() => setIndex(stepIndex)}
              aria-pressed={index === stepIndex}
              style={{
                padding: "0.35rem 0.7rem",
                borderRadius: "0.4rem",
                border: `1px solid ${index === stepIndex ? "var(--accent)" : "rgba(128,128,128,0.5)"}`,
                background: "transparent",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              {stepIndex + 1}
            </button>
          ))}
        </div>
      )}

      <figcaption style={{ color: "var(--muted)", fontSize: "0.85rem" }}>{t("caption")}</figcaption>
    </figure>
  );
}
